/**
 * Pricing strategies (pricing-strategies design, Task A, 2026-09-03 —
 * docs/plans/2026-09-03-pricing-strategies-design.md §1). Each strategy is
 * a pure function that picks tried prices for the SAME scenario map
 * price-calibration.ts's rows already use (CalibrationList.scenarios) —
 * nothing here is persisted or sent to Amazon (hard rule 5). Every money
 * number a caller shows the user is computed by calling price-calibration.
 * ts's existing scenario math (rowScenarioResult / buildScenarioSummary),
 * never re-implemented here.
 */
import type { PriceCalibrationRow } from '@sourcito/schemas';
import {
  buildScenarioSummary,
  estimatedSellThroughDays,
  rowScenarioResult,
  selectedUnitsForRow,
  signedMoney,
  SIMULATOR_STEP_CENTS,
} from './price-calibration';
// InventoryScope is price-calibration.ts's own import from profit-scenario.ts
// (not re-exported there) — same type, imported from its actual home.
import type { InventoryScope } from './profit-scenario';

// ─────────────────────────────────────────────────────────
// Strategy catalog
// ─────────────────────────────────────────────────────────

export type StrategyKey =
  | 'max-profit'
  | 'break-even'
  | 'sell-fast'
  | 'cut-losses'
  | 'sensible'
  | 'clearance';

export const STRATEGY_OPTIONS: { key: StrategyKey; label: string; description: string }[] = [
  {
    key: 'max-profit',
    label: 'Maximize profit',
    description: 'Raise every row priced below its target price up to target.',
  },
  {
    key: 'break-even',
    label: 'Break even',
    description: 'Set each row to its lowest no-loss price from landed cost and fees.',
  },
  {
    key: 'sell-fast',
    label: 'Sell through fast',
    description: 'Undercut the buy box or lowest competitor, never below break-even.',
  },
  {
    key: 'cut-losses',
    label: 'Cut losses',
    description:
      'Exit underwater rows at break-even where the market allows, else match the market and take the loss.',
  },
  {
    key: 'sensible',
    label: 'Sensible changes',
    description:
      'Cut losses on underwater rows, nudge fast movers toward target, discount slow movers — leaves the rest alone.',
  },
  {
    key: 'clearance',
    label: 'Clearance (competitor stock)',
    description:
      "Use each row's clearance price from competitor stock. Rows without one are left alone.",
  },
];

export type StrategyOption = (typeof STRATEGY_OPTIONS)[number];

/** Task C wires this in as a disabled placeholder (`data-slot="ask-ai"`) at
 *  the bottom of the Strategy menu, below a separator — Task E (§3) enables
 *  it and feeds the AI route's proposals/reasons into the same
 *  activeStrategy/reasonTags state a rule strategy fills today. Kept as
 *  data, not inline JSX strings, so its shape is directly unit-testable —
 *  the menu item itself lives behind a Radix Portal that never mounts under
 *  this suite's `renderToStaticMarkup` (no DOM), so the pure shape is what
 *  a static-render test can actually assert on. */
export const ASK_AI_MENU_ITEM = {
  label: 'Ask AI…',
  disabled: true,
  dataSlot: 'ask-ai',
} as const;

// ─────────────────────────────────────────────────────────
// Reason / skip vocabulary (§1)
// ─────────────────────────────────────────────────────────

export type ReasonTag =
  | 'to-target'
  | 'under-buybox'
  | 'under-competitor'
  | 'at-min-price'
  | 'break-even'
  | 'exit-at-market'
  | 'raised-selling-well'
  | 'cut-slow-mover'
  | 'held'
  | 'clearance';

export type SkipReason =
  | 'no-cost-basis'
  | 'no-price'
  | 'no-market-data'
  | 'already-there'
  | 'out-of-stock'
  | 'no-clearance-price';

export const REASON_LABELS: Record<ReasonTag, string> = {
  'to-target': 'to target',
  'under-buybox': 'under buy box',
  'under-competitor': 'under competitor',
  'at-min-price': 'at min price',
  'break-even': 'break-even',
  'exit-at-market': 'exit at market',
  'raised-selling-well': 'raised, selling well',
  'cut-slow-mover': 'cut, slow mover',
  held: 'held',
  clearance: 'clearance',
};

export const SKIP_LABELS: Record<SkipReason, string> = {
  'no-cost-basis': 'no cost basis',
  'no-price': 'no price',
  'no-market-data': 'no market data',
  'already-there': 'already there',
  'out-of-stock': 'out of stock',
  'no-clearance-price': 'no clearance price',
};

// ─────────────────────────────────────────────────────────
// Shared building blocks (§1)
// ─────────────────────────────────────────────────────────

type CostBasisRow = Pick<PriceCalibrationRow, 'costCents' | 'referralPct' | 'fixedFeeCents'>;
type AnchorRow = Pick<
  PriceCalibrationRow,
  'buyboxIsYours' | 'buyboxPriceCents' | 'lowestCompetitorCents'
>;

/** Price where unit profit is exactly zero: ceil((cost + fixedFee) / (1 -
 *  referralPct)), rounded UP to the cent. Null when cost/fee data is
 *  missing, or referralPct >= 1 (division by zero or a sign flip — never a
 *  valid fee structure). */
export function breakEvenPriceCents(row: CostBasisRow): number | null {
  if (row.costCents == null || row.referralPct == null || row.fixedFeeCents == null) return null;
  if (row.referralPct >= 1) return null;
  return Math.ceil((row.costCents + row.fixedFeeCents) / (1 - row.referralPct));
}

/** Lowest whole-cent price whose rounded fee calculation does not lose
 *  money. Kept separate from the existing conservative break-even floor so
 *  adding the explicit Break even strategy does not change other strategies. */
export function lowestNoLossPriceCents(row: CostBasisRow): number | null {
  let priceCents = breakEvenPriceCents(row);
  if (priceCents === null) return null;
  const profitAt = (price: number) =>
    price - Math.round(row.referralPct! * price + row.fixedFeeCents!) - row.costCents!;
  while (priceCents > 0 && profitAt(priceCents - 1) >= 0) priceCents -= 1;
  return priceCents;
}

/** Competitive anchor + which field backed it: we hold the buy box ->
 *  lowest competitor (falls back to the buy box price itself); we don't ->
 *  the buy box price (falls back to lowest competitor). Null when neither
 *  is known. `buyboxIsYours === null` (never resolved) is treated the same
 *  as `false`. The single source of truth for the fallback order — both
 *  `marketAnchorCents` and the under-buybox/under-competitor reason tag
 *  read it, so they can never drift from each other. */
export function marketAnchor(
  row: AnchorRow
): { cents: number; source: 'buybox' | 'competitor' } | null {
  if (row.buyboxIsYours === true) {
    if (row.lowestCompetitorCents != null) {
      return { cents: row.lowestCompetitorCents, source: 'competitor' };
    }
    if (row.buyboxPriceCents != null) return { cents: row.buyboxPriceCents, source: 'buybox' };
    return null;
  }
  if (row.buyboxPriceCents != null) return { cents: row.buyboxPriceCents, source: 'buybox' };
  if (row.lowestCompetitorCents != null) {
    return { cents: row.lowestCompetitorCents, source: 'competitor' };
  }
  return null;
}

/** Thin wrapper over `marketAnchor` for callers that only need the price
 *  (kept for the existing tests/call sites). */
export function marketAnchorCents(row: AnchorRow): number | null {
  return marketAnchor(row)?.cents ?? null;
}

function anchorReasonTag(row: AnchorRow): 'under-competitor' | 'under-buybox' {
  return marketAnchor(row)?.source === 'competitor' ? 'under-competitor' : 'under-buybox';
}

/** One simulator step ($0.25) under the anchor — the page's existing step. */
export function undercutCents(anchor: number): number {
  return anchor - SIMULATOR_STEP_CENTS;
}

// ─────────────────────────────────────────────────────────
// Sensible thresholds — "one place, not user-tunable in v1" (§1).
// ─────────────────────────────────────────────────────────

export const SENSIBLE_FAST_RUNWAY_DAYS = 45;
export const SENSIBLE_SLOW_RUNWAY_DAYS = 120;

// ─────────────────────────────────────────────────────────
// Per-row decisions
// ─────────────────────────────────────────────────────────

export interface StrategyResult {
  scenarios: Map<string, number>;
  reasons: Map<string, ReasonTag>;
  skipped: Array<{ sellerSku: string; why: SkipReason }>;
  lossTakenCents: number | null;
  /** Loss-eligible rows (underwater, considered by Cut losses/Sensible)
   *  whose proposed remaining-inventory profit came back null — most
   *  commonly a `supplier_x_pack` cost basis with no costed lot yet
   *  (`landedUnitCostCents` null), which scenarioRowInputForRow requires
   *  and margin/verdict math does not. These rows are NOT included in
   *  `lossTakenCents` (there's no dollar figure to add), so this count is
   *  how a caller shows "N more rows took a loss we can't size yet"
   *  instead of silently reading them as a $0 loss. */
  lossUnvaluedRowCount: number;
}

type Decision =
  | { priceCents: number; reason: ReasonTag }
  | { skip: SkipReason }
  // Reached the end of a Sensible branch with nothing to change — always
  // reason 'held', skip 'already-there'. A distinct marker (rather than
  // returning `{ skip: 'already-there' }` directly) so this one case is
  // handled in exactly one place (applyStrategy) instead of every branch
  // re-deriving "this fall-through means held".
  | { held: true };

/** Rows with verdict no-price/no-cost-basis, or zero selected units in
 *  scope, are always skipped ahead of any strategy-specific logic (§1). */
function alwaysSkipReason(row: PriceCalibrationRow, scope: InventoryScope): SkipReason | null {
  if (row.verdict === 'no-price') return 'no-price';
  if (row.verdict === 'no-cost-basis') return 'no-cost-basis';
  if (selectedUnitsForRow(row, scope) <= 0) return 'out-of-stock';
  return null;
}

function decideMaxProfit(row: PriceCalibrationRow): Decision {
  if (row.yourPriceCents == null) return { skip: 'no-price' };
  if (row.targetPriceCents == null) return { skip: 'no-cost-basis' };
  if (row.yourPriceCents < row.targetPriceCents) {
    return { priceCents: row.targetPriceCents, reason: 'to-target' };
  }
  return { skip: 'already-there' };
}

/** The row's clearance price (competitor stock design, 2026-10-05). Rows
 *  with no clearance, or one with no price (exit, fix the listing), get no
 *  proposal. */
function decideClearance(row: PriceCalibrationRow): Decision {
  const priceCents = row.clearance?.priceCents ?? null;
  if (priceCents === null) return { skip: 'no-clearance-price' };
  if (priceCents === row.yourPriceCents) return { skip: 'already-there' };
  return { priceCents, reason: 'clearance' };
}

function decideBreakEven(row: PriceCalibrationRow): Decision {
  if (row.yourPriceCents == null) return { skip: 'no-price' };
  const priceCents = lowestNoLossPriceCents({
    costCents: row.landedUnitCostCents,
    referralPct: row.referralPct,
    fixedFeeCents: row.fixedFeeCents,
  });
  if (priceCents == null) return { skip: 'no-cost-basis' };
  if (priceCents === row.yourPriceCents) return { skip: 'already-there' };
  return { priceCents, reason: 'break-even' };
}

type SellFastPick =
  | { priceCents: number; bound: 'min' | 'break-even' | 'anchor' }
  | { skip: SkipReason };

/** anchor = marketAnchorCents(row); anchor null -> minPriceCents (floor);
 *  else -> max(undercut(anchor), breakEven) — the shared sell-through-fast
 *  price rule, reused by both the standalone strategy and Sensible's
 *  slow-mover branch (which applies the identical rule under a different
 *  reason tag). */
function pickSellFastPrice(row: PriceCalibrationRow): SellFastPick {
  const anchor = marketAnchorCents(row);
  if (anchor == null) {
    if (row.minPriceCents == null) return { skip: 'no-market-data' };
    return { priceCents: row.minPriceCents, bound: 'min' };
  }
  const be = breakEvenPriceCents(row);
  if (be == null) return { skip: 'no-cost-basis' };
  const under = undercutCents(anchor);
  if (be >= under) return { priceCents: be, bound: 'break-even' };
  return { priceCents: under, bound: 'anchor' };
}

function decideSellFast(row: PriceCalibrationRow): Decision {
  if (row.yourPriceCents == null) return { skip: 'no-price' };
  const pick = pickSellFastPrice(row);
  if ('skip' in pick) return pick;
  if (pick.priceCents >= row.yourPriceCents) return { skip: 'already-there' };
  const reason: ReasonTag =
    pick.bound === 'min'
      ? 'at-min-price'
      : pick.bound === 'break-even'
        ? 'break-even'
        : anchorReasonTag(row);
  return { priceCents: pick.priceCents, reason };
}

/** be = breakEven; anchor = marketAnchorCents. anchor null or be <= anchor
 *  -> exit at break-even; else -> exit at min(lowest competitor ?? anchor,
 *  be) (never above break-even on an exit — pricing above break-even would
 *  be a profit, not an exit; N4). Shared by the standalone Cut losses
 *  strategy and Sensible's underwater branch. */
function decideCutLosses(row: PriceCalibrationRow): Decision {
  if (row.yourPriceCents == null) return { skip: 'no-price' };
  const be = breakEvenPriceCents(row);
  if (be == null) return { skip: 'no-cost-basis' };
  const anchor = marketAnchorCents(row);

  let priceCents: number;
  let reason: ReasonTag;
  if (anchor == null || be <= anchor) {
    priceCents = be;
    reason = 'break-even';
  } else {
    const marketPriceCents = row.lowestCompetitorCents ?? anchor;
    priceCents = Math.min(marketPriceCents, be);
    reason = priceCents === be ? 'break-even' : 'exit-at-market';
  }

  if (priceCents === row.yourPriceCents) return { skip: 'already-there' };
  return { priceCents, reason };
}

/** Known-zero velocity (recentUnitsPerDay === 0) is treated as "slow" the
 *  same as unknown velocity — an infinite runway is at least as slow as
 *  the 120-day cutoff, and the design's OR list would otherwise leave a
 *  SKU with zero observed sales un-flagged (N8: design §1 amended to say
 *  this explicitly). Deliberately different from `isSlowMoving` in
 *  price-calibration.ts: that one calls `estimatedSellThroughDays`, which
 *  returns null (not a huge number) for a known-zero rate, so a zero-
 *  velocity row never trips its `days > 90` check. `isSlowMoving` answers
 *  "is this row's sell-through demonstrably slow"; this one answers "does
 *  Sensible have anything useful to do with this row's velocity", where
 *  unknown and zero both mean no — not the same question. */
function isSensibleSlow(row: PriceCalibrationRow, scope: InventoryScope): boolean {
  if (!row.velocityKnown) return true;
  if (row.recentUnitsPerDay === 0) return true;
  const days = estimatedSellThroughDays(selectedUnitsForRow(row, scope), row.recentUnitsPerDay);
  return days !== null && days > SENSIBLE_SLOW_RUNWAY_DAYS;
}

/** The full §1 "selling well" branch condition, including "current <
 *  target" — a row already at or above target does not enter this branch
 *  at all (it falls through to the slow check next), matching the design's
 *  branch ordering rather than letting decideSensibleRaise's own
 *  already-there skip stand in for that fall-through. */
function isSensibleSellingWell(row: PriceCalibrationRow, scope: InventoryScope): boolean {
  if (!row.velocityKnown || row.recentUnitsPerDay == null || row.recentUnitsPerDay <= 0) {
    return false;
  }
  if (row.yourPriceCents == null || row.targetPriceCents == null) return false;
  if (row.yourPriceCents >= row.targetPriceCents) return false;
  const days = estimatedSellThroughDays(selectedUnitsForRow(row, scope), row.recentUnitsPerDay);
  return days !== null && days < SENSIBLE_FAST_RUNWAY_DAYS;
}

/** min(target, anchor ?? target), never above the anchor when we don't
 *  hold the buy box (§1). Also never proposes a price at or below the
 *  row's CURRENT price — a "raise" branch that would silently discount a
 *  well-selling row (an anchor already undercutting us) is treated as
 *  nothing to do (I4: routes to the `held` marker), not a hidden price cut
 *  (design silent on this edge; interpretation call — see report). */
function decideSensibleRaise(row: PriceCalibrationRow): Decision {
  if (row.yourPriceCents == null) return { skip: 'no-price' };
  if (row.targetPriceCents == null) return { skip: 'no-cost-basis' };
  if (row.yourPriceCents >= row.targetPriceCents) return { skip: 'already-there' };
  const anchor = marketAnchorCents(row);
  const cap = anchor ?? row.targetPriceCents;
  const priceCents = Math.min(row.targetPriceCents, cap);
  if (priceCents <= row.yourPriceCents) return { held: true };
  return { priceCents, reason: 'raised-selling-well' };
}

/** Sensible's slow-mover branch: the sell-through-fast price rule, tagged
 *  cut-slow-mover instead of the standalone strategy's more specific
 *  reason. Requires the row to sit above its own break-even (§1 "above
 *  break-even") — a slow row already at or below break-even has nothing
 *  valid to cut, so it falls through to the `held` marker (I4) rather than
 *  a plain skip. */
function decideSensibleCut(row: PriceCalibrationRow): Decision {
  if (row.yourPriceCents == null) return { skip: 'no-price' };
  const be = breakEvenPriceCents(row);
  if (be == null) return { skip: 'no-cost-basis' };
  if (row.yourPriceCents <= be) return { held: true };
  const pick = pickSellFastPrice(row);
  if ('skip' in pick) return pick;
  if (pick.priceCents >= row.yourPriceCents) return { skip: 'already-there' };
  return { priceCents: pick.priceCents, reason: 'cut-slow-mover' };
}

// ─────────────────────────────────────────────────────────
// applyStrategy
// ─────────────────────────────────────────────────────────

export function applyStrategy(
  key: StrategyKey,
  rows: PriceCalibrationRow[],
  scope: InventoryScope
): StrategyResult {
  const scenarios = new Map<string, number>();
  const reasons = new Map<string, ReasonTag>();
  const skipped: Array<{ sellerSku: string; why: SkipReason }> = [];
  // Rows whose loss/profit at the final price is eligible to count toward
  // lossTakenCents — every underwater row Cut losses/Sensible actually
  // considered, whether it ended up written to `scenarios` or landed
  // exactly back on its already-there skip.
  const lossEligible = new Set<string>();

  for (const row of rows) {
    const alwaysSkip = alwaysSkipReason(row, scope);
    if (alwaysSkip) {
      skipped.push({ sellerSku: row.sellerSku, why: alwaysSkip });
      continue;
    }

    let decision: Decision;

    if (key === 'max-profit') {
      decision = decideMaxProfit(row);
    } else if (key === 'break-even') {
      decision = decideBreakEven(row);
    } else if (key === 'sell-fast') {
      decision = decideSellFast(row);
    } else if (key === 'clearance') {
      decision = decideClearance(row);
    } else if (key === 'cut-losses') {
      // Outside this strategy's domain (I2: a skip, not a silent `continue`
      // — every row must land in either `scenarios` or `skipped` so
      // `scenarios.size + skipped.length === rows.length` always holds).
      // No reason tag: 'held' is Sensible-specific vocabulary.
      if (row.verdict !== 'underwater') {
        decision = { skip: 'already-there' };
      } else {
        lossEligible.add(row.sellerSku);
        decision = decideCutLosses(row);
      }
    } else {
      // sensible
      if (row.verdict === 'underwater') {
        lossEligible.add(row.sellerSku);
        decision = decideCutLosses(row);
      } else if (isSensibleSellingWell(row, scope)) {
        decision = decideSensibleRaise(row);
      } else if (isSensibleSlow(row, scope)) {
        decision = decideSensibleCut(row);
      } else {
        decision = { held: true };
      }
    }

    if ('held' in decision) {
      // I4: every Sensible fall-through — the plain "otherwise held" case
      // above, decideSensibleRaise's anchor-already-at-or-below-current
      // case, and decideSensibleCut's already-at-break-even case — lands
      // here, in exactly one place, so the reason tag can never be missed.
      reasons.set(row.sellerSku, 'held');
      skipped.push({ sellerSku: row.sellerSku, why: 'already-there' });
    } else if ('skip' in decision) {
      skipped.push({ sellerSku: row.sellerSku, why: decision.skip });
    } else {
      scenarios.set(row.sellerSku, decision.priceCents);
      reasons.set(row.sellerSku, decision.reason);
    }
  }

  let lossTakenCents: number | null = null;
  let lossUnvaluedRowCount = 0;
  if (lossEligible.size > 0) {
    let sum = 0;
    for (const row of rows) {
      if (!lossEligible.has(row.sellerSku)) continue;
      const profit = rowScenarioResult(row, scope, scenarios).proposed
        .remainingInventoryProfitCents;
      // I1: a loss-eligible row with no scenario-math cost basis
      // (landedUnitCostCents null — e.g. a supplier_x_pack row with no
      // costed lot yet) returns a null proposed profit, NOT a $0 one.
      // Counting it here instead of adding 0 to `sum` keeps the caller
      // from reading "no lot yet" as "no loss."
      if (profit == null) {
        lossUnvaluedRowCount += 1;
      } else if (profit < 0) {
        sum += profit;
      }
    }
    lossTakenCents = sum;
  }

  return { scenarios, reasons, skipped, lossTakenCents, lossUnvaluedRowCount };
}

// ─────────────────────────────────────────────────────────
// Result summary — pure, computed from the existing scenario math only.
// ─────────────────────────────────────────────────────────

export interface StrategyResultSummary {
  changed: number;
  skipped: Array<{ why: SkipReason; count: number }>;
  // N1: profitDeltaCents ("profit if it all sells", before vs after) and
  // sellThroughDeltaCents (the existing per-row "change from today", summed)
  // coincide by construction — both are sums over the same before/after
  // scenario pair, just accumulated through different aggregate fields.
  // Both are kept anyway: they're the two numbers named separately in the
  // design doc (§1 "Result summary"), and a future scenario where they
  // diverge (e.g. a partial-sell projection) should be a visible bug, not
  // a field silently reused for two meanings.
  profitDeltaCents: number | null;
  sellThroughDeltaCents: number | null;
  lossTakenCents: number | null;
  lossUnvaluedRowCount: number;
}

export function strategyResultSummary(
  result: StrategyResult,
  rows: PriceCalibrationRow[],
  scope: InventoryScope,
  // I3: the "after" summary reads whichever map is passed here, defaulting
  // to the strategy's own result — a caller (the page) passes its LIVE
  // scenario map instead once the operator hand-edits a tried price, so
  // the summary line never drifts from what's actually on screen.
  scenarios: ReadonlyMap<string, number> = result.scenarios
): StrategyResultSummary {
  const before = buildScenarioSummary(rows, scope, new Map());
  const after = buildScenarioSummary(rows, scope, scenarios);
  const valued = after.aggregate.financial.valuedRowCount > 0;

  const skipCounts = new Map<SkipReason, number>();
  for (const s of result.skipped) {
    skipCounts.set(s.why, (skipCounts.get(s.why) ?? 0) + 1);
  }

  return {
    changed: result.scenarios.size,
    skipped: [...skipCounts.entries()].map(([why, count]) => ({ why, count })),
    profitDeltaCents: valued
      ? after.aggregate.financial.remainingInventoryProfitCents -
        before.aggregate.financial.remainingInventoryProfitCents
      : null,
    sellThroughDeltaCents: valued ? after.aggregate.financial.sellThroughProfitDeltaCents : null,
    lossTakenCents: result.lossTakenCents,
    lossUnvaluedRowCount: result.lossUnvaluedRowCount,
  };
}

// ─────────────────────────────────────────────────────────
// Strategy chip label (Task C) — pure text builder for the state-line chip.
// Money is always run through price-calibration.ts's signedMoney (never
// re-formatted here), so the chip can never disagree with the summary
// cards above it about what a number means.
// ─────────────────────────────────────────────────────────

/** e.g. "Sell through fast: 27 changed, 4 left alone · +$1,240.00 if it all
 *  sells", plus " · loss taken -$310.00" when lossTakenCents is not null,
 *  plus " (N not valued)" when lossUnvaluedRowCount > 0 — appended to
 *  whichever segment lossTakenCents itself is part of. "Left alone" sums
 *  every skip-reason bucket in `summary.skipped`, not just one reason. */
export function strategyChipLabel(
  option: Pick<StrategyOption, 'label'>,
  summary: StrategyResultSummary
): string {
  const leftAlone = summary.skipped.reduce((sum, s) => sum + s.count, 0);
  let label = `${option.label}: ${summary.changed} changed, ${leftAlone} left alone`;
  label += ` · ${summary.profitDeltaCents !== null ? signedMoney(summary.profitDeltaCents) : '—'} if it all sells`;
  if (summary.lossTakenCents !== null) {
    label += ` · loss taken ${signedMoney(summary.lossTakenCents)}`;
  }
  if (summary.lossUnvaluedRowCount > 0) {
    label += ` (${summary.lossUnvaluedRowCount} not valued)`;
  }
  return label;
}
